import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Anchor, Box, PencilIcon, Text } from '@common-fe/common-fe';

import { useGetEmployeeByIdQuery } from '@/modules/employee/Employee/queries';
import { CheckDetails, DestinationType, LockboxStatus } from '@/modules/employer/components/Lockbox/lockbox.types';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { getFullAddress, isAddressFilled } from '@/utils';

import { BadAddressChip } from './BadAddressChip';
import { BadAddressWarning } from './BadAddressWarning';
import EditCheckAddressModal from './EditCheckAddressModal';

interface Props {
  currentLockboxStatus?: LockboxStatus;
}

const CheckAddress: React.FC<Props> = ({ currentLockboxStatus }) => {
  const [isEditCheckAddressModalVisible, setIsEditCheckAddressModalVisible] = useState(false);
  const { editableLockbox, setEditableLockbox } = useLockboxDetailsStore();
  const hasCheck = !!editableLockbox?.check;
  const employeeId = useMemo(() => editableLockbox?.claimRepayments?.[0]?.employeeId, [editableLockbox]);
  const hasApproveStatus = useMemo(() => currentLockboxStatus === LockboxStatus.APPROVED, [currentLockboxStatus]);
  const hasPartiallyApproveStatus = useMemo(() => currentLockboxStatus === LockboxStatus.PARTIALLY_APPROVED, [currentLockboxStatus]);
  const { employee, isFetching: isEmployeeFetching } = useGetEmployeeByIdQuery({ employeeId, disabled: !hasPartiallyApproveStatus });

  const isRemainderAddressFilled = useMemo(() =>
    isAddressFilled({
      line1: editableLockbox?.remainder?.addressLine1,
      city: editableLockbox?.remainder?.city,
      state: { name: editableLockbox?.remainder?.state },
      zipcode: editableLockbox?.remainder?.zip,
    }), [editableLockbox?.remainder]);

  const address = useMemo(() => {
    if (isRemainderAddressFilled) {
      return {
        line1: editableLockbox?.remainder?.addressLine1,
        line2: editableLockbox?.remainder?.addressLine2,
        city: editableLockbox?.remainder?.city,
        state: { name: editableLockbox?.remainder?.state },
        zipcode: editableLockbox?.remainder?.zip,
        undeliverable: editableLockbox?.remainder?.undeliverable,
      };
    }

    return {
      line1: employee?.mailingAddress?.line1,
      line2: employee?.mailingAddress?.line2,
      city: employee?.mailingAddress?.city,
      state: { name: employee?.mailingAddress?.state },
      zipcode: employee?.mailingAddress?.zipcode,
      undeliverable: employee?.mailingAddress?.undeliverable,
    };
  }, [isRemainderAddressFilled, editableLockbox, employee]);

  const isCheckAddressFilled = useMemo(() => isAddressFilled(address), [address]);

  const checkDetails: CheckDetails = useMemo((): CheckDetails => ({
    vendorName: editableLockbox?.remainder?.payTo || employee?.fullName,
    payTo: editableLockbox?.remainder?.payTo || employee?.fullName,
    addressLine1: address?.line1,
    addressLine2: address?.line2,
    city: address?.city,
    state: address?.state?.name,
    zip: address?.zipcode,
    undeliverable: address?.undeliverable,
  }), [address, editableLockbox, employee]);

  const handleOpenEditCheckAddressModal = useCallback(() => {
    setIsEditCheckAddressModalVisible(true);
  }, []);

  const isCheckDetailsInitialized = useMemo(() => !isEmployeeFetching, [isEmployeeFetching]);

  useEffect(() => {
    if (isCheckDetailsInitialized && employeeId) {
      setEditableLockbox({ remainder: {
        ...editableLockbox?.remainder,
        ...checkDetails,
        ...hasApproveStatus || hasPartiallyApproveStatus ? { benefitOf: checkDetails?.vendorName } : {},
      } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckDetailsInitialized, employeeId, hasApproveStatus, hasPartiallyApproveStatus]);

  useEffect(() => {
    if (!employeeId && !hasCheck && (hasApproveStatus || hasPartiallyApproveStatus)) {
      setEditableLockbox({ remainder: { destinationType: DestinationType.CHECK } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, hasCheck, hasApproveStatus, hasPartiallyApproveStatus]);  

  return (
    <>
      {isCheckAddressFilled ? (
        <Text>
          <Text data-testId="check-address" margin={{ right: 'spacing12' }} weight={700}>{getFullAddress(address)}</Text>
          <PencilIcon
            color="iconSecondary"
            size="16px"
            onClick={handleOpenEditCheckAddressModal}
            cursor="pointer"
          />
        </Text>
      ) : (
        <Anchor data-testId="add-check-address" size="medium" onClick={handleOpenEditCheckAddressModal}>Add address</Anchor>
      )}

      {editableLockbox?.remainder?.undeliverable ? (
        <Box margin={{ top: 'spacing12' }}>
          <BadAddressChip />
          <BadAddressWarning
            margin={{ top: 'spacing12' }}
            handleOpenEditCheckAddressModal={handleOpenEditCheckAddressModal}
          />
        </Box>
      ) : null}

      {isEditCheckAddressModalVisible && (
        <EditCheckAddressModal
          onSetVisible={setIsEditCheckAddressModalVisible}
          checkDetails={checkDetails}
        />
      )}
    </>
  );
};

export default CheckAddress;
